import { useCallback, useMemo } from "react";

import { losAcquisitionsApi } from "../../api/losAcquisitionsApi";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import {
  ACQUISITION_STATUSES,
  PIPELINES,
  PIPELINE_KEYS,
} from "../../pipelines/pipelineConfig";
import PipelineTableScreen from "../PipelineTableScreen/PipelineTableScreen";
import { buildAcquisitionsColumnDefs } from "./acquisitionsColumnDefs";

/**
 * Acquisitions (postclose) pipeline — the same board as Pre-Close, just fed by
 * `/loan_acquisitions/acquisitions_pipeline` and sliced by the postclose trade
 * statuses. Reuses the shared PipelineTableScreen (toolbar, saved views, status
 * tab strip, quick filter, column state), so it behaves identically.
 *
 * Row click opens the SAME drilldown under /acquisition/:id, which
 * PipelineProvider re-points at the acquisitions router.
 */
const AcquisitionsPipelineScreen = () => {
  useDocumentTitle("Acquisitions Pipeline");

  const drilldownBase = PIPELINES[PIPELINE_KEYS.ACQUISITIONS].drilldownBase;
  // Acquisition-specific columns (trade tape + bulk-trade linkage + postclose
  // key dates), with the Loan # link pointed at the acquisitions drilldown.
  const columnDefs = useMemo(
    () => buildAcquisitionsColumnDefs(drilldownBase),
    [drilldownBase],
  );

  const fetchData = useCallback(({ onSuccessFn, onFailFn, onErrorFn }) => {
    const onError = onFailFn || onErrorFn || (() => {});
    losAcquisitionsApi
      .getAcquisitionsPipeline()
      // The endpoint already returns only acquisition deals — no client-side
      // slice needed (unlike Pre-Close, which carves up the shared UW payload).
      .then((deals) => onSuccessFn?.(Array.isArray(deals) ? deals : []))
      .catch(onError);
  }, []);

  return (
    <PipelineTableScreen
      title="Acquisitions"
      storageKeyPrefix="acquisitions-pipeline"
      fetchData={fetchData}
      statusField="loanStatus"
      statusOptions={ACQUISITION_STATUSES}
      columnDefs={columnDefs}
      drilldownBase={drilldownBase}
    />
  );
};

export default AcquisitionsPipelineScreen;
